import React, { useState } from 'react'
import cn from 'classnames'
import { Loader2, Mail } from 'lucide-react'
import api from 'api/api'
import { cacheQuest } from 'state/cache'
import toast from 'utils/toast'
import { directMessagesEnabled } from 'lib/directMessages'
import styles from './message-user-button.module.scss'

type MessageableUser = {
    id?: string | number | null
    name?: string | null
    username?: string | null
    is_following_current_user?: boolean
}

type MessageUserButtonProps = {
    user?: MessageableUser | null
    // Opens the conversation thread; callers own panel placement.
    onOpenQuest: (questId: string, ev: React.MouseEvent) => void
    className?: string
    iconSize?: number
    testId?: string
}

// The full-width "Message" button under a profile (hover card, profile
// header). Opens (creating on first contact) the viewer's direct message with
// `user`. Anyone can be messaged; a conversation with someone who doesn't
// follow you starts as a message request (one message until they accept).
export default function MessageUserButton({
    user,
    onOpenQuest,
    className,
    iconSize = 16,
    testId = 'message-user-button',
}: MessageUserButtonProps) {
    const [opening, setOpening] = useState(false)

    const userId = user?.id == null ? null : String(user.id)
    const viewerId = gon.currentUser?.id == null ? null : String(gon.currentUser.id)
    if (!userId || !viewerId || userId === viewerId || !directMessagesEnabled()) return null

    const followsYou = user?.is_following_current_user === true

    const onClick = async (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (opening) return

        setOpening(true)
        try {
            const result = await api.openDirectMessage(userId)
            if (!result) return
            if (result.ok === false) {
                toast.error(result.error)
                return
            }
            cacheQuest(result.quest)
            onOpenQuest(String(result.quest.id), ev)
        } finally {
            setOpening(false)
        }
    }

    // Labeled, so no tooltip wrapper (which would also stop the button filling
    // its row); the request rule rides on title instead.
    return (
        <button
            type="button"
            className={cn(styles.wide, className)}
            onClick={onClick}
            title={
                followsYou
                    ? undefined
                    : "They don't follow you, so your first message is a request they can accept"
            }
            aria-busy={opening || undefined}
            data-testid={testId}
        >
            {opening ? (
                <Loader2 size={iconSize} className={styles.spinner} aria-hidden />
            ) : (
                <Mail size={iconSize} aria-hidden />
            )}
            <span>Message</span>
        </button>
    )
}
